import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { OtpInput } from "../components/OtpInput";
import { useRedirectTarget } from "../lib/navigation";
import { ApiError, api } from "../lib/api";
import { useSession } from "../lib/session";

export default function MfaVerify() {
  const { applyStep, signOut } = useSession();
  const navigate = useNavigate();
  const from = useRedirectTarget();

  const [code, setCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const verify = useCallback(async (body: { code: string } | { recoveryCode: string }) => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.mfaVerify(body);
      applyStep(result.step, true);
      navigate(from ?? "/", { replace: true });
    } catch (cause) {
      const apiError = cause instanceof ApiError ? cause : null;
      setError(apiError?.message ?? "That code could not be checked.");
      setCode("");
      if (apiError?.code === "mfa_enrolment_required") {
        applyStep("password", false);
        navigate("/mfa/set-up", { replace: true });
      }
    } finally {
      setBusy(false);
    }
  }, [applyStep, from, navigate]);

  return (
    <AuthLayout
      heading="Enter your code"
      blurb={
        useRecovery
          ? "Type one of the recovery codes you saved when you set up two-factor authentication. Each one works once."
          : "Open your authenticator app and type the six digits it shows for The Terraformed Life."
      }
    >
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (useRecovery) {
            if (recoveryCode.trim()) void verify({ recoveryCode: recoveryCode.trim() });
          } else if (code.length === 6) {
            void verify({ code });
          }
        }}
      >
        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}

        {useRecovery ? (
          <div className="field">
            <label htmlFor="recovery">Recovery code</label>
            <input
              id="recovery"
              className="input mono"
              autoFocus
              autoComplete="off"
              spellCheck={false}
              placeholder="xxxx-xxxx-xxxx"
              value={recoveryCode}
              onChange={(event) => setRecoveryCode(event.target.value)}
              aria-invalid={!!error || undefined}
            />
            <p className="hint">Dashes and capitals do not matter.</p>
          </div>
        ) : (
          <div className="field">
            <span className="label" id="verify-code-label">
              Six-digit code
            </span>
            <OtpInput
              value={code}
              onChange={setCode}
              onComplete={(value) => void verify({ code: value })}
              disabled={busy}
              autoFocus
              invalid={!!error}
              describedBy="verify-code-label"
            />
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={busy || (useRecovery ? !recoveryCode.trim() : code.length !== 6)}
        >
          {busy ? <span className="spinner" /> : null}
          {busy ? "Checking…" : "Verify and continue"}
        </button>
      </form>

      <div className="auth-alt">
        <button
          type="button"
          className="btn btn-quiet btn-sm"
          onClick={() => {
            setUseRecovery((previous) => !previous);
            setError(null);
            setCode("");
            setRecoveryCode("");
          }}
        >
          {useRecovery ? "Use my authenticator app" : "Use a recovery code instead"}
        </button>
        <button
          type="button"
          className="btn btn-quiet btn-sm"
          onClick={async () => {
            await signOut();
            navigate("/sign-in", { replace: true });
          }}
        >
          Sign in as someone else
        </button>
      </div>
    </AuthLayout>
  );
}
